import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { annonceService } from '../../api';
import './DetailAnnonce.css';
import { FiMapPin, FiTag, FiDollarSign, FiCalendar, FiUser, FiPhone, FiMail } from 'react-icons/fi';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Header from '../Header/Header';
import Footer from '../footer/Footer';

// Configuration des icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DetailAnnonce = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [annonce, setAnnonce] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPosition, setUserPosition] = useState([51.505, -0.09]);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchAnnonce = async () => {
      try {
        const response = await annonceService.getOne(id);
        const annonceData = response.data;
        
        console.log('Données complètes de l\'annonce:', annonceData);
        
     // Dans votre useEffect, modifiez la construction de l'URL :
if (annonceData.image) {
  // Si l'image est déjà une URL complète
  if (annonceData.image.startsWith('http')) {
    annonceData.imageUrl = annonceData.image;
  } 
  // Si c'est un chemin relatif
  else {
    // Solution 1 :
    annonceData.imageUrl = `${process.env.REACT_APP_API_URL}/storage/annonces/${annonceData.image.split('/').pop()}`;
    
    // Ou Solution 2 :
    // annonceData.imageUrl = `${process.env.REACT_APP_API_URL}/storage/annonces/${annonceData.image.split('/').pop()}`;
  }
  console.log('URL de l\'image:', annonceData.imageUrl);
}
        
        setAnnonce(annonceData);
        
        if (annonceData.user?.latitude && annonceData.user?.longitude) {
          setUserPosition([annonceData.user.latitude, annonceData.user.longitude]);
        }
      } catch (err) {
        console.error('Erreur détaillée:', err);
        setError(err.response?.data?.message || err.message || 'Erreur lors du chargement de l\'annonce');
      } finally {
        setLoading(false);
      }
    };

    fetchAnnonce();
  }, [id]);

  if (loading) return <div className="loading">Chargement...</div>;
  if (error) return <div className="error">Erreur: {error}</div>;
  if (!annonce) return null;

  return (
    <div className="detail-annonce-container">
      <Header />
      
      {/* Section image avec gestion d'erreur améliorée */}
      <div className="annonce-hero">
        {annonce.imageUrl && !imageError ? (
          <div className="annonce-image-container">
            <img 
              src={annonce.imageUrl} 
              alt={annonce.titre}
              className="annonce-image"
              onError={(e) => {
                console.error('Échec du chargement de l\'image:', annonce.imageUrl);
                setImageError(true);
                e.target.src = '/default-annonce.jpg';
              }}
            />
          </div>
        ) : (
          <div className="image-placeholder">
            <img 
              src="/default-annonce.jpg" 
              alt="Image par défaut" 
              className="default-image"
            />
            <p>{imageError ? "L'image n'a pas pu être chargée" : "Aucune image disponible"}</p>
          </div>
        )}
      </div>

      <div className="annonce-main-content">
        <div className="annonce-details-section">
          <div className="annonce-header">
            <h1>{annonce.titre}</h1>
            <div className="price-tag">{parseFloat(annonce.prix).toFixed(2)} €</div>
          </div>

          <div className="annonce-meta">
            <div className="meta-item">
              <FiTag className="icon" />
              <span>{annonce.type_annonce === 'offre' ? 'Offre de service' : 'Demande de service'}</span>
            </div>
            <div className="meta-item">
              <FiMapPin className="icon" />
              <span>{annonce.ville}</span>
            </div>
            <div className="meta-item">
              <FiCalendar className="icon" />
              <span>Publié le {new Date(annonce.created_at).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}</span>
            </div>
          </div>

          <div className="annonce-description">
            <h3>Description</h3>
            <p>{annonce.description}</p>
          </div>

          <div className="annonce-service">
            <h3>Service</h3>
            <div className="service-badge">
              {annonce.sous_service?.nom || 'Non spécifié'}
            </div>
          </div>

          <div className="map-section">
            <h3><FiMapPin className="icon" /> Localisation</h3>
            <div className="map-container">
              <MapContainer 
                center={userPosition} 
                zoom={13} 
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={userPosition}>
                  <Popup>
                    {annonce.user?.name || "Utilisateur"} <br /> 
                    {annonce.ville}
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        </div>

        <div className="user-contact-section">
          <div className="user-card">
            <div className="user-header">
              <FiUser className="user-icon" />
              <h3>{annonce.user?.name || "Anonyme"}</h3>
            </div>
            
            <div className="user-info">
              {annonce.user?.telephone && (
                <div className="info-item">
                  <FiPhone className="icon" />
                  <span>{annonce.user.telephone}</span>
                </div>
              )}
              {annonce.user?.email && (
                <div className="info-item">
                  <FiMail className="icon" />
                  <span>{annonce.user.email}</span>
                </div>
              )}
            </div>

            <div className="action-buttons">
              <button 
                className="primary-btn"
                onClick={() => window.location.href = `tel:${annonce.user?.telephone}`}
                disabled={!annonce.user?.telephone}
              >
                {annonce.user?.telephone ? 'Contacter' : 'Tél. non disponible'}
              </button>
              <button 
                className="secondary-btn"
                onClick={() => navigate('/message', { 
                  state: { 
                    destinataireId: annonce.user?.id, 
                    annonceId: annonce.id 
                  }
                })}
                disabled={!annonce.user?.id}
              >
                Envoyer un message
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default DetailAnnonce;